/**
 * The arrival, once per session.
 *
 * Once is the whole design. A welcome that appears on every navigation is a
 * thing people learn to dismiss without reading, and an operator who has
 * learned to dismiss her without reading is exactly the operator who will
 * dismiss the one message that mattered. It is keyed to sessionStorage for the
 * same reason the sign-in gate is: closing the tab ends the session, so the
 * next arrival is a real arrival.
 *
 * This is also the one place she is allowed to lead with urgent work unasked,
 * and that is deliberate. Arrival is the moment an operator is *expecting* to
 * be told where things stand; the same information pushed at them forty minutes
 * later, mid-work-order, is an interruption. So the worst open items lead here,
 * and nowhere else surfaces them on its own.
 *
 * Three things in order: what is worst, what moved while you were away, and
 * then the way in.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import s from "./assistant.module.css";
import { AssistantMark } from "./AssistantMark";
import av from "./avatar.module.css";
import { useAssistant } from "./AssistantContext";
import { useSession } from "../app/SessionContext";
import { welcomeBack } from "./greeting";
import { doneSince, signOffBrief, urgent, type UrgentReport } from "./digest";
import { lastVisit, recordVisit, sinceWords } from "./preferences";
import { clearSignOff, putSignOff } from "./signOff";
import { Button } from "../design/components";
import { t } from "../i18n";

const SEEN_KEY = "skylogics.assistant.greeted";

function alreadyGreeted(): boolean {
  try {
    return sessionStorage.getItem(SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

function markGreeted(): void {
  try {
    sessionStorage.setItem(SEEN_KEY, "1");
  } catch {
    /* a browser that refuses storage greets twice; that is the harmless failure */
  }
}

export function Welcome() {
  const session = useSession();
  const a = useAssistant();
  const [show, show_] = useState(() => !alreadyGreeted());
  /* Minimised by default: the picture and one line. An operator who wants the
     state of the airfield opens it; one who came in to do a job is not made to
     dismiss a panel first. */
  const [expanded, setExpanded] = useState(false);
  /* The countdown pauses while the operator is reading or reaching for it. */
  const [held, setHeld] = useState(false);
  const [report, setReport] = useState<UrgentReport | null>(null);
  const [moved, setMoved] = useState<string[]>([]);
  const cardRef = useRef<HTMLDivElement>(null);
  const regionRef = useRef<HTMLDivElement>(null);

  /* Read before the visit is recorded, or "last time" becomes "just now". */
  const previous = useMemo(() => lastVisit(a.who), [a.who]);

  useEffect(() => {
    let live = true;
    clearSignOff();

    urgent(session.source, session.site.id, session.site.code)
      .then((u) => live && setReport(u))
      .catch(() => live && setReport(null));

    doneSince(session.source, session.site.id, session.site.code, previous)
      .then((d) => live && !d.empty && setMoved(d.lines))
      .catch(() => {});

    /* Leave the handover note while the session still exists to read it. */
    signOffBrief(session.source, session.site.id, session.site.code)
      .then((lines) =>
        putSignOff({
          name: session.principal.name,
          lines,
          at: Date.now(),
          /* Carried with the note: by the time it is read, the session that
             knows this operator's preferences no longer exists. */
          speak: a.prefs.greet && a.prefs.voice.greetAloud,
          voice: {
            voiceURI: a.prefs.voice.voiceURI || undefined,
            rate: a.prefs.voice.rate,
            pitch: a.prefs.voice.pitch,
          },
        }),
      )
      .catch(() => {});

    return () => {
      live = false;
    };
  }, [
    a.prefs.greet,
    a.prefs.voice.greetAloud,
    a.prefs.voice.pitch,
    a.prefs.voice.rate,
    a.prefs.voice.voiceURI,
    previous,
    session.principal.name,
    session.site.code,
    session.site.id,
    session.source,
  ]);

  /*
   * She does not take focus.
   *
   * The card used to focus itself, which was right when it was a modal and is
   * wrong now that it is a corner panel: it stole the caret from whatever the
   * operator was doing. It also broke the countdown — the programmatic focus
   * tripped the same "someone is reaching for this" hold that a real one does,
   * so she greeted and then stayed forever.
   */

  /*
   * Say it out loud, once.
   *
   * Browsers will not make a sound until the page has been interacted with, and
   * this runs after the press that entered the platform — so the activation is
   * already there. It is spoken rather than read from the panel because a
   * greeting is the one thing worth hearing: two sentences at the start of a
   * shift, not a narrated screen. `spokenGreeting` guards against React's
   * double-invoked effects saying it twice.
   */
  const said = useRef(false);
  useEffect(() => {
    if (!show || !a.prefs.greet || !a.prefs.voice.greetAloud || said.current) return;
    said.current = true;
    const line = `${welcomeBack(session.principal.name, new Date(), previous !== null)} How can I help you today?`;
    a.readAloud(line);
  }, [a, previous, session.principal.name, show]);

  useEffect(() => {
    if (!show) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  /* Read when the countdown runs out, which is long after the render that
     started it. */
  const busy = useRef(false);
  busy.current = a.conversing || a.onCall || a.listening;

  function close() {
    markGreeted();
    recordVisit(a.who);
    show_(false);
    /* The card goes; she stays if she is in a conversation. Withdrawing her
       here closed the panel under a question she was still answering, and
       with it closed the microphone never reopened for the next one. */
    if (!busy.current) a.withdraw();
  }

  /*
   * She says hello, asks, and waits a moment before getting out of the way.
   *
   * Fifteen seconds, counted from when she stops speaking rather than from
   * when the card appears — a countdown that runs underneath her own voice
   * expires while she is still talking. Long enough to answer her question;
   * short enough that an operator who came in to do a job is not made to
   * dismiss her. It is held while the pointer is on the card or something in
   * it has focus, because a panel that vanishes as you reach for its button
   * is worse than one that overstays.
   */
  /* Arriving means being on screen; she withdraws when the greeting closes. */
  useEffect(() => {
    if (show && a.prefs.greet) a.show();
  }, [a, show]);

  useEffect(() => {
    if (!show || a.speaking || held || expanded) return;
    const timer = window.setTimeout(close, 15_000);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, a.speaking, held, expanded]);

  /*
   * A greeting never stands between the operator and the page. Clicking
   * anywhere else puts it away — whoever reached past her came to do a job —
   * and however long her voice claims to still be speaking (Safari does not
   * always say when speech ends), she is gone within forty-five seconds unless
   * the pointer is on her or the detail is open. Before this she could stay
   * over the top of every page for good, and the work-orders button under her
   * looked broken.
   */
  useEffect(() => {
    if (!show) return;
    const onDown = (e: PointerEvent) => {
      if (!(e.target instanceof Node) || !regionRef.current || regionRef.current.contains(e.target)) return;
      /* her own panel is her, not the page: talking to her must not put her away */
      if (e.target instanceof Element && e.target.closest("[data-sky-dock]")) return;
      close();
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (!show || held || expanded) return;
    const cap = window.setTimeout(close, 45_000);
    return () => window.clearTimeout(cap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, held, expanded]);

  /* An operator who turned the greeting off still counts as having arrived,
     so the visit is recorded and the handover note is still left. */
  useEffect(() => {
    if (show && !a.prefs.greet) {
      markGreeted();
      recordVisit(a.who);
      show_(false);
    }
  }, [a.prefs.greet, a.who, show]);

  if (!show || !a.prefs.greet) return null;

  const worst = report?.items.slice(0, 2) ?? [];

  return (
    <div
      ref={regionRef}
      className={s.arrival}
      role="region"
      aria-label={t("Sky greeting")}
      onPointerEnter={() => setHeld(true)}
      onPointerLeave={() => setHeld(false)}
      onFocusCapture={() => setHeld(true)}
    >
      <AssistantMark
        state={a.speaking ? "speaking" : "idle"}
        /* 192 tall × 1.5 = the 288 × 192 card: three inches by two. */
        size={192}
        variant={a.prefs.mark}
        image={a.prefs.markImage}
        className={av.pop}
      />

      <div className={s.arrivalCard} ref={cardRef} tabIndex={-1} aria-labelledby="assistant-greeting">
        <div className={s.arrivalHead}>
          <p className={s.welcomeGreeting} id="assistant-greeting">
            {welcomeBack(session.principal.name, new Date(), previous !== null)}
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-label={expanded ? t("Hide the detail") : t("Show what is open")}
          >
            {expanded
              ? t("Less")
              : a.due.length
                ? t("{n} due", { n: a.due.length })
                : worst.length
                  ? t("{n} open", { n: worst.length })
                  : t("More")}
          </Button>
          <Button variant="ghost" size="sm" onClick={close} aria-label={t("Close")} data-testid="greeting-close">
            ×
          </Button>
        </div>

        <p className={s.welcomeLine}>{t("How can I help you today?")}</p>

        {expanded && (
          <>
            {/* Her introduction was lost when this card was minimised; it belongs
                here, where somebody has asked for more rather than been given it. */}
            <p className={s.welcomeLine}>
              {t(
                "I am Sky. I read this platform's records, I can raise work and plan it, and I can explain any screen or field — ask me for a tutorial. I never decide anything; that stays yours.",
              )}
            </p>
            {previous !== null && (
              <p className={s.welcomeLine}>{t("You were last here {when}.", { when: sinceWords(previous) })}</p>
            )}

            {worst.length > 0 && (
              <div className={s.welcomeFacts}>
                <div>{t("Worst open now:")}</div>
                {worst.map((u) => (
                  <div key={u.id}>
                    · {u.label} — {u.because}
                  </div>
                ))}
              </div>
            )}

            {a.due.length > 0 && (
              <div className={s.welcomeFacts}>
                <div>{t("Running out:")}</div>
                {a.due.slice(0, 3).map((d) => (
                  <div key={d.id}>
                    · {d.label} — {d.detail}
                  </div>
                ))}
                {a.due.length > 3 && <div>· {t("and {n} more, under Due.", { n: a.due.length - 3 })}</div>}
              </div>
            )}

            {moved.length > 0 && (
              <div className={s.welcomeFacts}>
                <div>{t("Since you were last here:")}</div>
                {moved.map((l, i) => (
                  <div key={i}>· {l}</div>
                ))}
              </div>
            )}

            {session.source.kind === "mock" && (
              <p className={s.welcomeLine}>
                {t("This session is on the built-in sample, so none of the above is real.")}
              </p>
            )}
          </>
        )}

        <div className={s.welcomeActions}>
          <Button
            variant="ghost"
            onClick={() => {
              close();
              a.summon("What should I look at first?");
            }}
          >
            {t("What should I look at?")}
          </Button>
          <Button variant="primary" onClick={close}>
            {t("Start")}
          </Button>
        </div>
      </div>
    </div>
  );
}
